import { incidentTypeEnum, type IncidentType } from '@/gen/types/IncidentType';

import { actionableObservationFieldRules } from './actionable-observation';
import { capacityPerformanceFieldRules } from './capacity-performance';
import { chemicalSpillEmissionsFieldRules } from './chemical-spill-emissions';
import { changeOrConfigurationErrorFieldRules } from './change-or-configuration-error';
import { coolingHvacFieldRules } from './cooling-hvac';
import { customerSatisfactionFieldRules } from './customer-satisfaction';
import { facilityInfrastructureFieldRules } from './facility-infrastructure';
import { fireExplosionFieldRules } from './fire-explosion';
import { generalIncidentFieldRules } from './general-incident';
import { generalObservationFieldRules } from './general-observation';
import { informationSecurityCybersecurityFieldRules } from './information-security-cybersecurity';
import { itSystemsNetworkFieldRules } from './it-systems-network';
import { legalRegulatoryFieldRules } from './legal-regulatory';
import { networkInfrastructureFieldRules } from './network-infrastructure';
import { peopleIncidentFieldRules } from './people-incident';
import { physicalInfrastructureFieldRules } from './physical-infrastructure';
import { physicalSecurityFieldRules } from './physical-security';
import { positiveObservationFieldRules } from './positive-observation';
import { policyComplianceFieldRules } from './policy-compliance';
import { powerElectricalSystemsFieldRules } from './power-electrical-systems';
import { serviceQualityContractualDeviationFieldRules } from './service-quality-contractual-deviation';
import { supplierPerformanceFieldRules } from './supplier-performance';
import { thirdPartyFieldRules } from './third-party';
import type { IncidentFieldRule } from './types';
import { wasteManagementFieldRules } from './waste-management';

const observationIncidentTypes = new Set<IncidentType>([
    incidentTypeEnum.ActionableObservation,
    incidentTypeEnum.PositiveObservation,
]);

const specificFieldRulesByIncidentType: Partial<Record<IncidentType, IncidentFieldRule[]>> = {
    [incidentTypeEnum.ActionableObservation]: actionableObservationFieldRules,
    [incidentTypeEnum.PositiveObservation]: positiveObservationFieldRules,
    [incidentTypeEnum.NetworkInfrastructure]: networkInfrastructureFieldRules,
    [incidentTypeEnum.FacilityInfrastructure]: facilityInfrastructureFieldRules,
    [incidentTypeEnum.CapacityPerformance]: capacityPerformanceFieldRules,
    [incidentTypeEnum.ChangeOrConfigurationError]: changeOrConfigurationErrorFieldRules,
    [incidentTypeEnum.PhysicalInfrastructure]: physicalInfrastructureFieldRules,
    [incidentTypeEnum.CoolingHVAC]: coolingHvacFieldRules,
    [incidentTypeEnum.PowerElectricalSystems]: powerElectricalSystemsFieldRules,
    [incidentTypeEnum.CustomerSatisfaction]: customerSatisfactionFieldRules,
    [incidentTypeEnum.LegalRegulatory]: legalRegulatoryFieldRules,
    [incidentTypeEnum.PolicyCompliance]: policyComplianceFieldRules,
    [incidentTypeEnum.SupplierPerformance]: supplierPerformanceFieldRules,
    [incidentTypeEnum.ServiceQualityContractualDeviation]: serviceQualityContractualDeviationFieldRules,
    [incidentTypeEnum.ITSystemsNetwork]: itSystemsNetworkFieldRules,
    [incidentTypeEnum.PhysicalSecurity]: physicalSecurityFieldRules,
    [incidentTypeEnum.PeopleIncident]: peopleIncidentFieldRules,
    [incidentTypeEnum.FireExplosion]: fireExplosionFieldRules,
    [incidentTypeEnum.ChemicalSpillEmissions]: chemicalSpillEmissionsFieldRules,
    [incidentTypeEnum.WasteManagement]: wasteManagementFieldRules,
    [incidentTypeEnum.ThirdParty]: thirdPartyFieldRules,
    [incidentTypeEnum.InformationSecurityCybersecurity]: informationSecurityCybersecurityFieldRules,
};

export function getIncidentFieldRules(incidentType: IncidentType): IncidentFieldRule[] {
    const sharedRules = (
        observationIncidentTypes.has(incidentType) ? generalObservationFieldRules : generalIncidentFieldRules
    ).map((rule) => ({
        requireSchemaField: true,
        ...rule,
    }));
    const specificRules = specificFieldRulesByIncidentType[incidentType] ?? [];
    return [...sharedRules, ...specificRules];
}
